import React, { useEffect, useState } from "react";
import {
  Form,
  InputNumber,
  Button,
  Select,
  message,
  Row,
  Col,
  Drawer,
  Typography,
  Input,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import API from "../../api";
import { useSelector } from "react-redux";

const { Option } = Select;
const { Text } = Typography;

const AddStoreProductForm = ({ open, setOpen, data }) => {
  const [form] = Form.useForm();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [user, setUser] = useState(null);
  const [str, setStr] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [productDetails, setProductDetails] = useState({
    quantity: 1,
    batchNumber: "",
    tracking_number: "",
    purchasePrice: 0,
    sellingPrice: 0,
    mrp: 0,
    barcode: "",
    freeQuantity: 0,
    gst: { id: null, percentage: 0 },
  });
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [filterStocked, setFilterStocked] = useState(undefined);

  const productSettings = useSelector(
    (state) => state.productSetting?.productSetting
  );

  const [storeSettings, setStoreSettings] = useState({
    trackingNumber: "No",
    batchNumber: "No",
    barcode: "No",
  });

  const fetchUser = () => {
    const userData = JSON.parse(localStorage?.getItem("userData") || "{}");
    setUser(userData);

    if (userData) {
      if (userData.role === "admin") {
        const settings = productSettings.find(
          (setting) => setting.storeId === selectedStoreId
        );
        setStoreSettings({
          trackingNumber: settings?.engineNumber || "No",
          batchNumber: settings?.batchNumber || "No",
          barcode: settings?.barcode || "No",
        });
      } else if (userData.storeId) {
        const settings = productSettings.find(
          (setting) => setting.storeId === userData.storeId
        );
        setStoreSettings({
          trackingNumber: settings?.engineNumber || "No",
          batchNumber: settings?.batchNumber || "No",
          barcode: settings?.barcode || "No",
        });
      }
    }
  };

  const fetchStores = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response?.data || []);
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Failed to fetch stores.");
    }
  };

  const fetchTax = async () => {
    try {
      const taxResponse = await API.get("/taxes");
      const taxData = taxResponse?.data || [];
      setTaxes(taxData);
      const defaultTax =
        taxData.find((tax) => tax.percentage === 18) || taxData[0];
      if (defaultTax) {
        setProductDetails((prev) => ({
          ...prev,
          gst: { id: defaultTax._id, percentage: defaultTax.percentage },
        }));
        form.setFieldsValue({ gstId: defaultTax._id });
      }
    } catch (error) {
      console.error("Error fetching taxes:", error);
      message.error("Failed to fetch taxes.");
    }
  };

  const fetchProducts = async (search = "") => {
    try {
      const params = {
        search,
        ...(filterStocked !== undefined && { isStocked: filterStocked }),
      };
      const response = await API.get("/products/paginate", { params });
      setProducts(response?.data?.products || response?.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      message.error("Failed to fetch products.");
    }
  };

  useEffect(() => {
    fetchUser();
    fetchStores();
    fetchTax();
    fetchProducts();
  }, [selectedStoreId]);

  // Set initial store based on data prop or user role
  useEffect(() => {
    if (data && data._id) {
      // If data prop is provided (from row click), prioritize it
      setSelectedStoreId(data._id);
      setStr(data);
      form.setFieldsValue({ storeId: data._id });
    } else if (user && stores.length > 0) {
      // Fallback to user-based store selection
      const store = stores.find((store) => store?._id === user?.storeId);
      setStr(store);
      if (store) {
        setSelectedStoreId(store?._id);
        form.setFieldsValue({ storeId: store?._id });
      } else if (stores.length === 1) {
        setSelectedStoreId(stores[0]?._id);
        form.setFieldsValue({ storeId: stores[0]?._id });
      }
    }
  }, [user, stores, data, form]);

  useEffect(() => {
    if (selectedProduct) {
      const newDetails = {
        quantity: 1,
        batchNumber: productDetails.batchNumber || "",
        tracking_number: productDetails.tracking_number || "",
        purchasePrice: selectedProduct?.pricing?.purchasePrice || 0,
        sellingPrice: selectedProduct?.pricing?.salePrice || 0,
        mrp: selectedProduct?.pricing?.mrpPrice || 0,
        barcode: productDetails.barcode || "",
        freeQuantity: 0,
        gst: productDetails.gst,
      };
      setProductDetails(newDetails);
      form.setFieldsValue({
        quantity: newDetails.quantity,
        batchNumber: newDetails.batchNumber,
        tracking_number: newDetails.tracking_number,
        purchasePrice: newDetails.purchasePrice,
        sellingPrice: newDetails.sellingPrice,
        mrp: newDetails.mrp,
        barcode: newDetails.barcode,
        freeQuantity: newDetails.freeQuantity,
        gstId: newDetails.gst.id,
      });
    } else {
      const defaultGst = taxes.find((tax) => tax.percentage === 18) ||
        taxes[0] || {
          id: null,
          percentage: 0,
        };
      setProductDetails({
        quantity: 1,
        batchNumber: "",
        tracking_number: "",
        purchasePrice: 0,
        sellingPrice: 0,
        mrp: 0,
        barcode: "",
        freeQuantity: 0,
        gst: defaultGst,
      });
      form.setFieldsValue({
        quantity: 1,
        batchNumber: "",
        tracking_number: "",
        purchasePrice: 0,
        sellingPrice: 0,
        mrp: 0,
        barcode: "",
        freeQuantity: 0,
        gstId: defaultGst.id,
      });
    }
  }, [selectedProduct, taxes, form]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setSelectedProduct(null);
      setProductDetails({
        quantity: 1,
        batchNumber: "",
        tracking_number: "",
        purchasePrice: 0,
        sellingPrice: 0,
        mrp: 0,
        barcode: "",
        freeQuantity: 0,
        gst: taxes.find((tax) => tax.percentage === 18) ||
          taxes[0] || {
            id: null,
            percentage: 0,
          },
      });
      // Do not reset selectedStoreId or str here to preserve store selection
    }
  }, [open, form, taxes]);

  const handleSubmit = async () => {
    if (!selectedProduct) {
      message.error("Please select a product");
      return;
    }
    try {
      const values = await form.validateFields();
      setLoading(true);
      const stockData = {
        storeId: values.storeId,
        productId: selectedProduct?._id,
        quantity: productDetails.quantity,
        batch: [
          {
            batchNumber:
              storeSettings.batchNumber === "Yes"
                ? productDetails.batchNumber || null
                : null,
            quantity: productDetails.quantity,
            tracking_number:
              storeSettings.trackingNumber === "Yes"
                ? productDetails.tracking_number || null
                : null,
            purchasePrice: productDetails.purchasePrice,
            sellingPrice: productDetails.sellingPrice,
            mrp: productDetails.mrp,
            barcode:
              storeSettings.barcode === "Yes"
                ? productDetails.barcode || null
                : null,
            freeQuantity: productDetails.freeQuantity,
            gst: productDetails.gst,
          },
        ],
      };
      const res = await API.post("/stocks/stock-add", stockData);
      if (res.status === 200) {
        message.success("Stock updated successfully");
        // Preserve storeId in the form
        const currentStoreId = form.getFieldValue("storeId");
        // Reset form fields except storeId
        form.resetFields([
          "productId",
          "quantity",
          "batchNumber",
          "tracking_number",
          "purchasePrice",
          "sellingPrice",
          "mrp",
          "barcode",
          "freeQuantity",
          "gstId",
        ]);
        // Reset product-related states
        setSelectedProduct(null);
        setProductDetails({
          quantity: 1,
          batchNumber: "",
          tracking_number: "",
          purchasePrice: 0,
          sellingPrice: 0,
          mrp: 0,
          barcode: "",
          freeQuantity: 0,
          gst: taxes.find((tax) => tax.percentage === 18) ||
            taxes[0] || {
              id: null,
              percentage: 0,
            },
        });
        // Restore storeId to the form to ensure it persists
        form.setFieldsValue({ storeId: currentStoreId });
        fetchProducts(productSearch); // Refresh the product list with products where isStocked is false after successful submission
      }
    } catch (error) {
      console.error("Error updating stock:", error);
      message.error(
        error?.response?.data?.message || "Failed to update stock."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProductSearch = (value) => {
    setProductSearch(value);
    fetchProducts(value);
  };

  const handleFieldChange = (field, value) => {
    setProductDetails((prev) => {
      const updatedDetails = { ...prev, [field]: value };
      if (field === "gstId") {
        const selectedTax = taxes.find((tax) => tax._id === value);
        updatedDetails.gst = {
          id: value,
          percentage: selectedTax?.percentage || 0,
        };
      }
      return updatedDetails;
    });
    form.setFieldsValue({ [field]: value });
  };

  const formatNumber = (number) =>
    `₹${new Intl.NumberFormat("en-IN").format(number || 0)}`;

  return (
    <Drawer
      title={data ? "Edit Stock Product" : "Update Stock Product"}
      width={800}
      onClose={() => setOpen(false)}
      open={open}
      bodyStyle={{ padding: "16px" }}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={[12, 8]} align="middle">
          <Col xs={24} sm={8}>
            <Form.Item
              name="storeId"
              label="Store"
              rules={[{ required: true, message: "Select a store" }]}
            >
              <Select
                placeholder="Select a store"
                disabled={user?.role !== "admin" || !!data} // Disable if data is provided or user is not admin
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) => setSelectedStoreId(value)}
              >
                {stores.length === 0 ? (
                  <Option disabled>No stores available</Option>
                ) : (
                  stores.map((store) => (
                    <Option key={store?._id} value={store?._id}>
                      {store?.name}
                    </Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item
              name="productId"
              label="Product"
              rules={[{ required: true, message: "Select a product" }]}
            >
              <Select
                showSearch
                placeholder="Search and select a product"
                value={selectedProduct?._id}
                onChange={(value) => {
                  const product = products.find((prod) => prod._id === value);
                  setSelectedProduct(product);
                }}
                onSearch={handleProductSearch}
                filterOption={false}
                style={{ width: "100%" }}
              >
                {products.map((product) => (
                  <Option key={product._id} value={product._id}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <span>{`${
                        product.specifications?.name || "Unnamed Product"
                      } ${product.code || ""}`}</span>
                      <span>{formatNumber(product?.pricing?.salePrice)}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[12, 8]}>
          <Col xs={24} sm={6}>
            <Form.Item
              name="quantity"
              label="Quantity"
              rules={[{ required: true, message: "Enter quantity" }]}
            >
              <InputNumber
                min={1}
                value={productDetails.quantity}
                onChange={(value) => handleFieldChange("quantity", value)}
                style={{ width: "100%" }}
                placeholder="Qty"
              />
            </Form.Item>
          </Col>
          {storeSettings.batchNumber === "Yes" && (
            <Col xs={24} sm={6}>
              <Form.Item
                name="batchNumber"
                label="Batch Number"
                rules={[
                  {
                    required: true,
                    message: "Enter batch number",
                  },
                ]}
              >
                <Input
                  value={productDetails.batchNumber}
                  onChange={(e) =>
                    handleFieldChange("batchNumber", e.target.value)
                  }
                  style={{ width: "100%" }}
                  placeholder="Batch No."
                />
              </Form.Item>
            </Col>
          )}
          {storeSettings.trackingNumber === "Yes" && (
            <Col xs={24} sm={6}>
              <Form.Item
                name="tracking_number"
                label="Tracking Number"
                rules={[
                  {
                    required: true,
                    message: "Enter tracking number",
                  },
                ]}
              >
                <Input
                  value={productDetails.tracking_number}
                  onChange={(e) =>
                    handleFieldChange("tracking_number", e.target.value)
                  }
                  style={{ width: "100%" }}
                  placeholder="Tracking No."
                />
              </Form.Item>
            </Col>
          )}
          {storeSettings.barcode === "Yes" && (
            <Col xs={24} sm={6}>
              <Form.Item
                name="barcode"
                label="Barcode"
                rules={[{ required: true, message: "Enter barcode" }]}
              >
                <Input
                  value={productDetails.barcode}
                  onChange={(e) => handleFieldChange("barcode", e.target.value)}
                  style={{ width: "100%" }}
                  placeholder="Barcode"
                />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={6}>
            <Form.Item
              name="mrp"
              label="MRP (₹)"
              rules={[{ required: true, message: "Enter MRP" }]}
            >
              <InputNumber
                min={0}
                value={productDetails.mrp}
                onChange={(value) => handleFieldChange("mrp", value)}
                style={{ width: "100%" }}
                formatter={(value) => formatNumber(value)}
                parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
                placeholder="MRP"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="sellingPrice"
              label="Selling Price (₹)"
              rules={[{ required: true, message: "Enter selling price" }]}
            >
              <InputNumber
                min={0}
                value={productDetails.sellingPrice}
                onChange={(value) => handleFieldChange("sellingPrice", value)}
                style={{ width: "100%" }}
                formatter={(value) => formatNumber(value)}
                parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
                placeholder="Selling Price"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="purchasePrice"
              label="Purchase Price (₹)"
              rules={[{ required: true, message: "Enter purchase price" }]}
            >
              <InputNumber
                min={0}
                value={productDetails.purchasePrice}
                onChange={(value) => handleFieldChange("purchasePrice", value)}
                style={{ width: "100%" }}
                formatter={(value) => formatNumber(value)}
                parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
                placeholder="Purchase Price"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name="gstId" label="GST">
              <Select
                value={productDetails.gst.id}
                onChange={(value) => handleFieldChange("gstId", value)}
                style={{ width: "100%" }}
                placeholder="GST Rate"
              >
                {taxes.length === 0 ? (
                  <Option disabled>No GST rates available</Option>
                ) : (
                  taxes.map((tax) => (
                    <Option key={tax._id} value={tax._id}>
                      {`${tax.percentage}%`}
                    </Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="end" style={{ marginTop: 8 }}>
          <Col>
            <Text strong>
              Total:{" "}
              {formatNumber(
                productDetails.quantity * productDetails.sellingPrice
              )}
            </Text>
          </Col>
        </Row>
        <Form.Item style={{ marginTop: 8 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            block
            size="large"
          >
            Update Stock
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AddStoreProductForm;
